import React, { useState, useMemo } from "react";

const RightPanel = ({ userData, userCourses }) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(today.getDate());

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const blanks = Array(firstDay).fill(null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const allDays = [...blanks, ...days];

  // ----------------------------------------------------------
  //  CLASS DATA MAPPING - Corrected to match actual data structure
  // ----------------------------------------------------------
  const classData = useMemo(() => {
    const map = {};
    
    userCourses?.forEach((schedule) => {
      // Access course data correctly
      const course = schedule.course;
      if (!schedule?.start_date || !schedule?.end_date) return;

      const startDate = new Date(schedule.start_date);
      const endDate = new Date(schedule.end_date);
      
      // Loop through all dates in the schedule range
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const key = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
        
        // Get instructor name from the correct path
        const instructor = course?.instructors?.[0]?.user?.full_name || "Instructor";
        
        if (!map[key]) map[key] = [];
        map[key].push({
          time: `${schedule.start_time?.substring(0, 5) || "09:00"} - ${schedule.end_time?.substring(0, 5) || "17:00"}`,
          title: course?.title || "Untitled Course",
          teacher: instructor,
          type: schedule.mode || course?.course_type || "In Person",
          location: schedule.location || "N/A",
        });
        
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
    });
    
    return map;
  }, [userCourses]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else setCurrentMonth(currentMonth - 1);
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else setCurrentMonth(currentMonth + 1);
  };

  const handleDateClick = (day) => {
    if (day) setSelectedDate(day);
  };

  const selectedKey = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(selectedDate).padStart(2, "0")}`;
  const selectedClasses = classData[selectedKey] || [];

  // ----------------------------------------------------------
  // PROFILE FALLBACK LOGIC
  // ----------------------------------------------------------
  const profileImage = userData?.avatar_url;
  const nameToUse = (userData?.full_name || userData?.name || "User").trim();
  const userInitials = nameToUse.split(" ").filter(Boolean).map((n) => n[0]).join("").toUpperCase() || "U";
  const userName = userData?.full_name || userData?.name || "User";
  const userEmail = userData?.email || "";

  return (
    <div className="w-full max-w-[300px] bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-6 shadow-sm mx-auto">
      
      {/* ---------------- PROFILE SECTION ---------------- */}
      <div className="flex flex-col items-center text-center">
        {profileImage ? (
          <img
            src={profileImage}
            alt="Profile"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "";
            }}
            className="w-28 h-28 rounded-full border-2 border-teal-400 object-cover"
          />
        ) : (
          <div className="w-28 h-28 rounded-full border-2 border-teal-400 overflow-hidden">
            <div className="w-full h-full bg-gradient-to-br from-[#4AACC5] to-[#3E95AC] flex items-center justify-center text-xl font-bold text-white">
              {userInitials}
            </div>
          </div>
        )}

        <h3 className="text-gray-800 text-base font-bold mt-3">{userName}</h3>
        <p className="text-gray-600 text-xs font-normal">{userEmail}</p>
      </div>

      {/* ---------------- CALENDAR SECTION ---------------- */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <button onClick={handlePrevMonth} className="text-gray-600 hover:text-teal-500">
            ◀
          </button>
          <h4 className="text-gray-800 text-sm font-semibold">
            {monthNames[currentMonth]} {currentYear}
          </h4>
          <button onClick={handleNextMonth} className="text-gray-600 hover:text-teal-500">
            ▶
          </button>
        </div>

        <div className="grid grid-cols-7 text-center text-xs gap-1 text-gray-700">
          {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
            <div key={d} className="font-semibold">{d}</div>
          ))}

          {allDays.map((d, i) => {
            if (!d) return <div key={i}></div>;

            const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            const hasClass = !!classData[dateKey];

            const isToday =
              d === today.getDate() &&
              currentMonth === today.getMonth() &&
              currentYear === today.getFullYear();

            const isSelected = d === selectedDate;

            return (
              <div
                key={i}
                onClick={() => handleDateClick(d)}
                className={`relative py-1 rounded-full cursor-pointer transition-all duration-150
                  ${isSelected
                    ? "text-white font-bold"
                    : isToday
                    ? "text-gray-800 font-semibold"
                    : hasClass
                    ? "font-semibold"
                    : "hover:bg-gray-100 text-gray-700"
                  }`}
                style={{
                  backgroundColor: isSelected ? "#4AACC5" : "transparent",
                  border: isToday ? "2px solid #4AACC5" : "none",
                  color: hasClass && !isSelected && !isToday ? "#4AACC5" : undefined
                }}
              >
                {d}
              </div>
            );
          })}
        </div>
      </div>

      {/* ---------------- CLASS LIST SECTION ---------------- */}
      <div>
        <h4 className="text-stone-900 text-lg font-medium mb-3">
          {monthNames[currentMonth]} {selectedDate}, {currentYear}
        </h4>

        {selectedClasses.length > 0 ? (
          <div className="relative pl-6 border-l border-gray-300">
            <div className="absolute -left-[7px] top-0 flex flex-col gap-20">
              {selectedClasses.map((_, i) => (
                <div key={i} className="w-3 h-3 bg-white rounded-full border border-gray-300"></div>
              ))}
            </div>

            {selectedClasses.map((cls, i) => (
              <div key={i} className="mb-6">
                <p className="text-[11px] text-gray-600 mb-1">{cls.time}</p>
                <div className="bg-white border border-gray-200 rounded-lg p-2">
                  <p className="text-gray-800 text-sm font-medium">{cls.title}</p>
                  <p className="text-gray-600 text-xs">{cls.teacher}</p>
                  {cls.location && (
                    <p className="text-gray-500 text-[10px] mt-1">📍 {cls.location}</p>
                  )}
                  <span className="inline-block mt-2 px-3 py-[2px] bg-cyan-100 text-teal-500 text-[10px] rounded-full">
                    {cls.type?.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500 text-sm">No classes on this date</p>
        )}
      </div>
    </div>
  );
};

export default RightPanel;